import { useCallback, useEffect, useMemo, useState } from "react";
import { BellRing, Loader2, AlertCircle, Smartphone } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { arrayBufferToBase64, isWebPushSupported, urlBase64ToUint8Array } from "@/lib/webPush";
import {
  getMessagingIntegrationDetail,
  getMessagingIntegrationLabel,
  groupMessagingIntegrationsByChannel,
  type MessagingIntegrationOption,
} from "@/lib/integration-display-label";
import { MESSAGING_CHANNEL_GROUPS } from "@/lib/messaging-integration-providers";

interface PushNotificationsSettingsProps {
  workspaceId: string;
  userId: string;
}

type PermState = "default" | "granted" | "denied";
type PrefKind = "messages" | "handoffs" | "assignments" | "tasks" | "integrations";

async function syncPushSubscriptionIntegrations(
  subscriptionId: string,
  allIntegrationIds: string[],
  selectedIntegrationIds: string[],
) {
  const useAll =
    selectedIntegrationIds.length === 0 ||
    (allIntegrationIds.length > 0 &&
      allIntegrationIds.every((id) => selectedIntegrationIds.includes(id)));

  if (useAll) {
    await supabase
      .from("push_subscription_integrations")
      .delete()
      .eq("subscription_id", subscriptionId);
    return;
  }

  const { data: existingRows, error: readError } = await supabase
    .from("push_subscription_integrations")
    .select("integration_id")
    .eq("subscription_id", subscriptionId);
  if (readError) throw readError;

  const existingIds = (existingRows ?? []).map((row) => row.integration_id as string);
  const toAdd = selectedIntegrationIds.filter((id) => !existingIds.includes(id));
  const toRemove = existingIds.filter((id) => !selectedIntegrationIds.includes(id));

  if (toAdd.length > 0) {
    const { error: insertError } = await supabase.from("push_subscription_integrations").insert(
      toAdd.map((integration_id) => ({
        subscription_id: subscriptionId,
        integration_id,
      })),
    );
    if (insertError) throw insertError;
  }

  if (toRemove.length > 0) {
    const { error: deleteError } = await supabase
      .from("push_subscription_integrations")
      .delete()
      .eq("subscription_id", subscriptionId)
      .in("integration_id", toRemove);
    if (deleteError) throw deleteError;
  }
}

function isStandalonePWA(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  const matchStandalone = typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches;
  return iosStandalone || matchStandalone;
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke("vapid-public-key", { method: "GET" });
    if (error) return null;
    const key = (data as { publicKey?: string | null; valid?: boolean } | null)?.publicKey ?? null;
    return typeof key === "string" && key.length > 0 ? key : null;
  } catch {
    return null;
  }
}

async function hashKey(key: string): Promise<string> {
  const enc = new TextEncoder().encode(key);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  const bytes = new Uint8Array(buf);
  return Array.from(bytes.slice(0, 8)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function subscriptionUsesVapidKey(sub: PushSubscription, vapidPublic: string): boolean {
  const actual = sub.options?.applicationServerKey;
  if (!actual) return true;
  const expected = urlBase64ToUint8Array(vapidPublic);
  const actualBytes = new Uint8Array(actual);
  return actualBytes.length === expected.length && actualBytes.every((byte, index) => byte === expected[index]);
}

async function ensureRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    let reg = await navigator.serviceWorker.getRegistration("/");
    if (!reg) {
      reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    }
    // Aguarda ativar (importante no iOS após instalar PWA)
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

export function PushNotificationsSettings({ workspaceId, userId }: PushNotificationsSettingsProps) {
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [savingPref, setSavingPref] = useState<PrefKind | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [notifyMessages, setNotifyMessages] = useState(true);
  const [notifyHandoffs, setNotifyHandoffs] = useState(true);
  const [notifyAssignments, setNotifyAssignments] = useState(true);
  const [notifyTasks, setNotifyTasks] = useState(true);

  const [browserPerm, setBrowserPerm] = useState<PermState>("default");
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [messagingIntegrations, setMessagingIntegrations] = useState<MessagingIntegrationOption[]>([]);
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<string[]>([]);

  const standalone = isStandalonePWA();
  const ios = isIOS();
  const allIntegrationIds = useMemo(
    () => messagingIntegrations.map((item) => item.id),
    [messagingIntegrations],
  );
  const groupedIntegrations = groupMessagingIntegrationsByChannel(messagingIntegrations);

  useEffect(() => {
    if (!workspaceId || !userId) return;

    void (async () => {
      const [{ data: integrations }, { data: accessibleIds }] = await Promise.all([
        supabase
          .from("integrations")
          .select("id, provider, config, instance_identifier, instagram_account_id, page_id, status")
          .eq("workspace_id", workspaceId)
          .in("status", ["active", "pending", "disconnected"])
          .in("provider", [
            "whatsapp_evolution",
            "whatsapp_cloud",
            "whatsapp_uazapi",
            "instagram",
            "messenger",
          ]),
        supabase.rpc("member_accessible_integration_ids", {
          _user_id: userId,
          _workspace_id: workspaceId,
        }),
      ]);

      const allowed = new Set((accessibleIds as string[] | null) ?? []);
      const filtered = ((integrations || []) as MessagingIntegrationOption[]).filter(
        (item) => allowed.has(item.id),
      );
      setMessagingIntegrations(filtered);
    })();
  }, [workspaceId, userId]);

  const loadIntegrationPrefs = useCallback(
    async (subId: string, integrations: MessagingIntegrationOption[]) => {
      const ids = integrations.map((item) => item.id);
      const { data } = await supabase
        .from("push_subscription_integrations")
        .select("integration_id")
        .eq("subscription_id", subId);
      const stored = (data || []).map((row) => row.integration_id as string);
      if (stored.length === 0) {
        setSelectedIntegrationIds(ids);
        return;
      }
      setSelectedIntegrationIds(stored.filter((id) => ids.includes(id)));
    },
    [],
  );

  // Repara automaticamente: se o navegador tem PushSubscription válida + permissão concedida,
  // mas a linha do banco sumiu (ex.: BadJwtToken antigo deletou), faz upsert silencioso.
  const autoRepair = useCallback(async (endpoint: string, sub: PushSubscription) => {
    try {
      const vapidPublic = await fetchVapidPublicKey();
      if (!vapidPublic) return false;
      if (!subscriptionUsesVapidKey(sub, vapidPublic)) return false;
      const keyHash = await hashKey(vapidPublic);
      const key = sub.getKey("p256dh");
      const auth = sub.getKey("auth");
      if (!key || !auth) return false;

      const { error } = await supabase.from("push_subscriptions").upsert(
        {
          user_id: userId,
          workspace_id: workspaceId,
          endpoint,
          p256dh: arrayBufferToBase64(key),
          auth_key: arrayBufferToBase64(auth),
          user_agent: navigator.userAgent.slice(0, 512),
          enabled: true,
          notify_messages: true,
          notify_handoffs: true,
          vapid_key_hash: keyHash,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,workspace_id,endpoint" },
      );
      if (error) {
        console.warn("push autoRepair upsert error:", error.message);
        return false;
      }
      return true;
    } catch (e) {
      console.warn("push autoRepair failed:", e);
      return false;
    }
  }, [userId, workspaceId]);

  const refreshState = useCallback(async () => {
    setChecking(true);
    try {
      if ("Notification" in window) {
        setBrowserPerm(Notification.permission as PermState);
      }

      let endpoint: string | null = null;
      let sub: PushSubscription | null = null;
      if ("serviceWorker" in navigator) {
        try {
          const reg = await navigator.serviceWorker.getRegistration("/");
          if (reg) {
            sub = await reg.pushManager.getSubscription();
            endpoint = sub?.endpoint ?? null;
          }
        } catch {
          /* noop */
        }
      }
      setCurrentEndpoint(endpoint);

      if (endpoint) {
        const vapidPublic = await fetchVapidPublicKey();
        const keyHash = vapidPublic ? await hashKey(vapidPublic) : null;
        const browserSubMatchesCurrentKey = !sub || !vapidPublic || subscriptionUsesVapidKey(sub, vapidPublic);

        const { data } = await supabase
          .from("push_subscriptions")
          .select("id, enabled, notify_messages, notify_handoffs, notify_assignments, notify_tasks, vapid_key_hash")
          .eq("user_id", userId)
          .eq("workspace_id", workspaceId)
          .eq("endpoint", endpoint)
          .maybeSingle();

        const hasValidBrowserSub =
          !!sub &&
          typeof Notification !== "undefined" &&
          Notification.permission === "granted" &&
          browserSubMatchesCurrentKey;

        if (data) {
          setSubscriptionId(data.id as string);
          if ((keyHash && data.vapid_key_hash && data.vapid_key_hash !== keyHash) || !browserSubMatchesCurrentKey) {
            try { await sub?.unsubscribe(); } catch { /* noop */ }
            setEnabled(false);
            return;
          }

          // Auto-reparo expandido: se a linha existe mas foi desabilitada
          // pelo backend (ex.: BadJwtToken da Apple) E o navegador ainda
          // tem uma subscription válida + permissão concedida, reabilita
          // silenciosamente — desde que o hash da chave VAPID ainda case.
          if (!data.enabled && hasValidBrowserSub) {
            let canRepair = true;
            try {
              const vapidPublic = await fetchVapidPublicKey();
              if (vapidPublic && data.vapid_key_hash) {
                const currentHash = await hashKey(vapidPublic);
                if (currentHash !== data.vapid_key_hash) canRepair = false;
              }
            } catch {
              /* falha ao validar hash não bloqueia o reparo */
            }

            if (canRepair) {
              const { error: repairErr } = await supabase
                .from("push_subscriptions")
                .update({
                  enabled: true,
                  consecutive_failures: 0,
                  last_failure_reason: null,
                  updated_at: new Date().toISOString(),
                })
                .eq("user_id", userId)
                .eq("workspace_id", workspaceId)
                .eq("endpoint", endpoint);
              if (!repairErr) {
                setEnabled(true);
                setNotifyMessages(Boolean(data.notify_messages));
                setNotifyHandoffs(Boolean(data.notify_handoffs));
                setNotifyAssignments(data.notify_assignments ?? true);
                setNotifyTasks(data.notify_tasks ?? true);
                await loadIntegrationPrefs(data.id as string, messagingIntegrations);
                return;
              }
            }
          }

          setEnabled(Boolean(data.enabled));
          setNotifyMessages(Boolean(data.notify_messages));
          setNotifyHandoffs(Boolean(data.notify_handoffs));
          setNotifyAssignments(data.notify_assignments ?? true);
          setNotifyTasks(data.notify_tasks ?? true);
          await loadIntegrationPrefs(data.id as string, messagingIntegrations);
        } else if (hasValidBrowserSub) {
          // Linha do banco sumiu: tenta auto-reparo com upsert silencioso
          const ok = await autoRepair(endpoint, sub!);
          if (ok) {
            setEnabled(true);
            setNotifyMessages(true);
            setNotifyHandoffs(true);
          } else {
            setEnabled(false);
          }
        } else {
          setSubscriptionId(null);
          setSelectedIntegrationIds(allIntegrationIds);
          setEnabled(false);
        }
      } else {
        setSubscriptionId(null);
        setSelectedIntegrationIds(allIntegrationIds);
        setEnabled(false);
      }
    } catch (e) {
      console.error("push check:", e);
    } finally {
      setChecking(false);
    }
  }, [userId, workspaceId, autoRepair, loadIntegrationPrefs, messagingIntegrations, allIntegrationIds]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  // Revalida quando o app volta do background no iPhone (PWA)
  useEffect(() => {
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        refreshState();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refreshState]);

  const activate = useCallback(async () => {
    if (!isWebPushSupported()) {
      toast({
        title: "Não suportado",
        description: "Use um navegador recente. No iPhone, instale o app na Tela de Início e abra pelo ícone.",
        variant: "destructive",
      });
      return;
    }
    if (ios && !standalone) {
      toast({
        title: "Instale o app primeiro",
        description: "No iPhone, toque em Compartilhar → Adicionar à Tela de Início. Depois abra pelo ícone e ative aqui.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const vapidPublic = await fetchVapidPublicKey();
      if (!vapidPublic) {
        toast({
          title: "Notificações indisponíveis",
          description: "Não foi possível configurar este dispositivo agora. Tente novamente em instantes.",
          variant: "destructive",
        });
        return;
      }

      const perm = await Notification.requestPermission();
      setBrowserPerm(perm as PermState);
      if (perm !== "granted") {
        toast({
          title: "Permissão negada",
          description: "Sem permissão, o navegador não pode enviar notificações.",
          variant: "destructive",
        });
        return;
      }

      const ready = await ensureRegistration();
      if (!ready) {
        toast({
          title: "Falha ao registrar dispositivo",
          description: "Não foi possível ativar o serviço de notificações. Recarregue o app e tente de novo.",
          variant: "destructive",
        });
        return;
      }

      // Se já há subscription, garantir que casa com a chave VAPID atual; senão, recriar.
      const keyHash = await hashKey(vapidPublic);
      let sub = await ready.pushManager.getSubscription();

      if (sub) {
        // Buscar hash salvo no banco (pode não existir se foi limpo após rotação).
        const { data: existing } = await supabase
          .from("push_subscriptions")
          .select("vapid_key_hash")
          .eq("user_id", userId)
          .eq("workspace_id", workspaceId)
          .eq("endpoint", sub.endpoint)
          .maybeSingle();

        // Se não há linha no banco OU o hash diverge da chave atual,
        // a subscription do navegador está vinculada a uma VAPID key antiga.
        // Precisamos cancelar e recriar com a chave nova.
        if (!existing || (existing.vapid_key_hash && existing.vapid_key_hash !== keyHash)) {
          try { await sub.unsubscribe(); } catch { /* noop */ }
          sub = null;
        }
      }

      if (!sub) {
        sub = await ready.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublic) as unknown as BufferSource,
        });
      }

      const key = sub.getKey("p256dh");
      const auth = sub.getKey("auth");
      if (!key || !auth) {
        toast({
          title: "Subscription inválida",
          description: "O navegador não devolveu as chaves de criptografia. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from("push_subscriptions").upsert(
        {
          user_id: userId,
          workspace_id: workspaceId,
          endpoint: sub.endpoint,
          p256dh: arrayBufferToBase64(key),
          auth_key: arrayBufferToBase64(auth),
          user_agent: navigator.userAgent.slice(0, 512),
          enabled: true,
          notify_messages: true,
          notify_handoffs: true,
          notify_assignments: true,
          notify_tasks: true,
          vapid_key_hash: keyHash,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,workspace_id,endpoint" },
      );

      if (error) throw error;

      const { data: savedSub } = await supabase
        .from("push_subscriptions")
        .select("id")
        .eq("user_id", userId)
        .eq("workspace_id", workspaceId)
        .eq("endpoint", sub.endpoint)
        .maybeSingle();

      setCurrentEndpoint(sub.endpoint);
      setSubscriptionId((savedSub?.id as string) ?? null);
      setEnabled(true);
      setNotifyMessages(true);
      setNotifyHandoffs(true);
      setNotifyAssignments(true);
      setNotifyTasks(true);
      setSelectedIntegrationIds(allIntegrationIds);

      toast({ title: "Notificações ativadas", description: "Você receberá alertas neste dispositivo." });
    } catch (e: unknown) {
      console.error("push subscribe:", e);
      toast({
        title: "Erro ao ativar",
        description: e instanceof Error ? e.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, userId, workspaceId, ios, standalone, allIntegrationIds]);

  const deactivate = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint: string | null = currentEndpoint;
      if (!endpoint && "serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration("/");
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            endpoint = sub.endpoint;
            try { await sub.unsubscribe(); } catch { /* noop */ }
          }
        }
      } else if (endpoint && "serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration("/");
        const sub = await reg?.pushManager.getSubscription();
        if (sub) {
          try { await sub.unsubscribe(); } catch { /* noop */ }
        }
      }

      if (endpoint) {
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", userId)
          .eq("workspace_id", workspaceId)
          .eq("endpoint", endpoint);
      }

      setEnabled(false);
      setCurrentEndpoint(null);
      setSubscriptionId(null);
      setSelectedIntegrationIds(allIntegrationIds);
      toast({ title: "Notificações desativadas neste dispositivo" });
    } catch (e: unknown) {
      console.error("push unsubscribe:", e);
      toast({
        title: "Erro ao desativar",
        description: e instanceof Error ? e.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, userId, workspaceId, currentEndpoint, allIntegrationIds]);

  const updateIntegrationPrefs = useCallback(
    async (nextIds: string[]) => {
      if (!subscriptionId) return;
      const prev = selectedIntegrationIds;
      setSelectedIntegrationIds(nextIds);
      setSavingPref("integrations");
      try {
        await syncPushSubscriptionIntegrations(subscriptionId, allIntegrationIds, nextIds);
      } catch (e) {
        setSelectedIntegrationIds(prev);
        toast({
          title: "Não foi possível salvar",
          description: e instanceof Error ? e.message : "Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setSavingPref(null);
      }
    },
    [subscriptionId, selectedIntegrationIds, allIntegrationIds, toast],
  );

  const handleMasterToggle = useCallback(
    (checked: boolean) => {
      if (checked) activate();
      else deactivate();
    },
    [activate, deactivate],
  );

  const updatePreference = useCallback(
    async (kind: Exclude<PrefKind, "integrations">, value: boolean) => {
      if (!currentEndpoint) return;
      const prev =
        kind === "messages" ? notifyMessages
        : kind === "handoffs" ? notifyHandoffs
        : kind === "assignments" ? notifyAssignments
        : notifyTasks;
      if (kind === "messages") setNotifyMessages(value);
      else if (kind === "handoffs") setNotifyHandoffs(value);
      else if (kind === "assignments") setNotifyAssignments(value);
      else setNotifyTasks(value);
      setSavingPref(kind);
      try {
        const patch =
          kind === "messages" ? { notify_messages: value }
          : kind === "handoffs" ? { notify_handoffs: value }
          : kind === "assignments" ? { notify_assignments: value }
          : { notify_tasks: value };
        const { error } = await supabase
          .from("push_subscriptions")
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq("user_id", userId)
          .eq("workspace_id", workspaceId)
          .eq("endpoint", currentEndpoint);
        if (error) throw error;
      } catch (e) {
        if (kind === "messages") setNotifyMessages(prev);
        else if (kind === "handoffs") setNotifyHandoffs(prev);
        else if (kind === "assignments") setNotifyAssignments(prev);
        else setNotifyTasks(prev);
        toast({
          title: "Não foi possível salvar",
          description: e instanceof Error ? e.message : "Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setSavingPref(null);
      }
    },
    [currentEndpoint, userId, workspaceId, notifyMessages, notifyHandoffs, notifyAssignments, notifyTasks, toast],
  );

  const isDenied = browserPerm === "denied" && !enabled;
  const needsStandalone = ios && !standalone;

  return (
    <div className="space-y-5">
      {/* Master switch */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BellRing className="w-5 h-5 text-primary" />
          <div>
            <Label className="text-sm font-medium">Notificações neste dispositivo</Label>
            <p className="text-xs text-muted-foreground">Receba alertas em tempo real.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {checking ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <>
              <Badge variant={enabled ? "default" : "secondary"} className="text-[10px]">
                {enabled ? "Ativadas" : "Desativadas"}
              </Badge>
              <Switch
                checked={enabled}
                onCheckedChange={handleMasterToggle}
                disabled={loading || isDenied || needsStandalone}
              />
            </>
          )}
        </div>
      </div>

      {needsStandalone && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs leading-relaxed">
          <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            No iPhone, é obrigatório <strong>instalar o app na Tela de Início</strong> (Compartilhar → Adicionar à Tela de Início)
            e abrir pelo ícone. Notificações em background não funcionam no Safari comum.
          </span>
        </div>
      )}

      {isDenied && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-xs leading-relaxed">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            As notificações foram bloqueadas neste dispositivo. Reative nas configurações do navegador/sistema.
          </span>
        </div>
      )}

      {/* Sub-preferences */}
      {enabled && (
        <div className="pl-8 border-l border-border space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Novas mensagens</Label>
              <p className="text-xs text-muted-foreground">Avisar quando o cliente enviar uma mensagem.</p>
            </div>
            <div className="flex items-center gap-2">
              {savingPref === "messages" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
              <Switch
                checked={notifyMessages}
                onCheckedChange={(v) => updatePreference("messages", v)}
                disabled={loading || savingPref !== null}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Escalações para atendimento humano</Label>
              <p className="text-xs text-muted-foreground">Avisar quando uma conversa for escalada.</p>
            </div>
            <div className="flex items-center gap-2">
              {savingPref === "handoffs" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
              <Switch
                checked={notifyHandoffs}
                onCheckedChange={(v) => updatePreference("handoffs", v)}
                disabled={loading || savingPref !== null}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Atribuições a você</Label>
              <p className="text-xs text-muted-foreground">Avisar quando um lead for atribuído a você.</p>
            </div>
            <div className="flex items-center gap-2">
              {savingPref === "assignments" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
              <Switch
                checked={notifyAssignments}
                onCheckedChange={(v) => updatePreference("assignments", v)}
                disabled={loading || savingPref !== null}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Lembretes de tarefas</Label>
              <p className="text-xs text-muted-foreground">Avisar quando uma tarefa estiver vencendo.</p>
            </div>
            <div className="flex items-center gap-2">
              {savingPref === "tasks" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
              <Switch
                checked={notifyTasks}
                onCheckedChange={(v) => updatePreference("tasks", v)}
                disabled={loading || savingPref !== null}
              />
            </div>
          </div>

          {messagingIntegrations.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div>
                <Label className="text-sm font-medium">Canais com push</Label>
                <p className="text-xs text-muted-foreground">
                  Somente canais que você pode acessar na inbox. Se todos estiverem marcados, você recebe push de todos.
                </p>
              </div>
              <div className="space-y-3 max-h-56 overflow-y-auto rounded border p-2">
                {MESSAGING_CHANNEL_GROUPS.map((group) => {
                  const items = groupedIntegrations[group.channel];
                  if (items.length === 0) return null;
                  const groupIds = items.map((item) => item.id);
                  const allChecked = groupIds.every((id) => selectedIntegrationIds.includes(id));
                  const someChecked =
                    groupIds.some((id) => selectedIntegrationIds.includes(id)) && !allChecked;

                  return (
                    <div key={group.channel} className="space-y-2">
                      <div className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5">
                        <span className="text-sm font-medium">{group.label}</span>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`push-channel-${group.channel}`}
                            checked={allChecked}
                            ref={(el) => {
                              if (el) {
                                (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someChecked;
                              }
                            }}
                            disabled={savingPref !== null}
                            onCheckedChange={(checked) => {
                              const next = checked
                                ? [...new Set([...selectedIntegrationIds, ...groupIds])]
                                : selectedIntegrationIds.filter((id) => !groupIds.includes(id));
                              void updateIntegrationPrefs(next);
                            }}
                          />
                          <label htmlFor={`push-channel-${group.channel}`} className="text-xs text-muted-foreground cursor-pointer">
                            Todos
                          </label>
                        </div>
                      </div>
                      <div className="grid gap-2 pl-2">
                        {items.map((integration) => (
                          <div key={integration.id} className="flex items-start space-x-3">
                            <Checkbox
                              id={`push-integration-${integration.id}`}
                              checked={selectedIntegrationIds.includes(integration.id)}
                              disabled={savingPref !== null}
                              className="mt-0.5"
                              onCheckedChange={(checked) => {
                                const next = checked
                                  ? [...selectedIntegrationIds, integration.id]
                                  : selectedIntegrationIds.filter((id) => id !== integration.id);
                                void updateIntegrationPrefs(next);
                              }}
                            />
                            <label htmlFor={`push-integration-${integration.id}`} className="text-sm cursor-pointer leading-snug">
                              <span className="font-medium">{getMessagingIntegrationLabel(integration)}</span>
                              {getMessagingIntegrationDetail(integration) ? (
                                <span className="block text-xs text-muted-foreground">
                                  {getMessagingIntegrationDetail(integration)}
                                </span>
                              ) : null}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {savingPref === "integrations" && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Salvando canais...
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground pl-8">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{enabled ? "Desativando..." : "Ativando..."}</span>
        </div>
      )}
    </div>
  );
}
